import React from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import useToasts from 'components/hooks/useToasts';

import ToastRack from 'components/application/ToastRack';

import ProjectActivityBlankNotice from 'components/projectActivity/ProjectActivityBlankNotice';
import ProjectActivityTable from 'components/projectActivity/ProjectActivityTable';

import { collateMessagesAndDispatches } from 'components/helpers/resources/messages';

export default function ProjectActivityTab({
  currentUser,
  downloadDisabled,
  editProject,
  isVersionPdfsEnabled,
  projectId,
  projectIncomplete,
}) {
  const [toasts, , , , addInedibleToast] = useToasts();
  const {
    data: histories,
    fetchNextPage: fetchNextHistoriesPage,
    hasNextPage: hasNextHistoriesPage,
    isFetchingNextPage: isFetchingNextHistoriesPage,
  } = useInfiniteQuery({
    queryKey: ['projectHistories', projectId],
    queryFn: async ({ pageParam = 1 }) =>
      (await getProjectHistories(projectId, pageParam)).data,
    getNextPageParam: (lastPage, _allPages) =>
      lastPage.meta.is_last_page ?
        undefined
      : parseInt(lastPage.meta.loaded_page_no, 10) + 1,
    onError: addInedibleToast,
  });

  const getProjectHistories = (projectId, pageParam) =>
    axios.get(`/projects/${projectId}/histories`, {
      params: { page_no: pageParam },
    });

  const allIncludedData = histories?.pages?.flatMap((page) => page.included);

  const formatMessageInfo = (histories) => {
    if (!histories) {
      return {};
    }

    const allCollatedData = collateMessagesAndDispatches(allIncludedData);
    return allCollatedData.reduce((accumulator, object) => {
      const id = Object.keys(object)[0];
      accumulator[id] = object[id];
      return accumulator;
    }, {});
  };

  const formatHistories = (histories) => {
    if (!histories) {
      return [];
    }

    const allHistoriesData = histories.pages.flatMap((page) => page.data);
    return allHistoriesData.map((history) => {
      const personnel = allIncludedData.find(
        (includedData) =>
          includedData.id === history.relationships?.personnel?.data?.id &&
          includedData.type === 'personnel',
      );

      const user = allIncludedData.find(
        (includedData) =>
          includedData.id === history.relationships?.user?.data?.id &&
          includedData.type === 'user',
      );

      const profilePhoto = allIncludedData.find((includedData) => {
        if (personnel) {
          return (
            includedData?.relationships?.personnel?.data?.id ===
              personnel?.id && includedData.type === 'profilePhoto'
          );
        }
        return (
          includedData?.relationships?.personnel?.data?.id ===
            user?.relationships?.personnel?.data?.id &&
          includedData.type === 'profilePhoto'
        );
      });

      return {
        id: history.id,
        reason: history.attributes.reason,
        createdAt: history.attributes.createdAt,
        displayNumber: history.attributes.displayNumber,
        nadminUser: history.attributes.nadminUserShortName,
        messageId:
          history.relationships.message.data &&
          history.relationships.message.data.id,
        versionId:
          history.relationships.version.data &&
          parseInt(history.relationships.version.data.id, 10),
        // set either personnel or user for the initials if a photo isn't available
        user: (personnel || user)?.attributes,
        profilePhoto: profilePhoto?.links,
        isApiActivity: history.attributes?.context?.includes('api'),
      };
    });
  };

  return (
    <>
      {projectIncomplete ?
        <ProjectActivityBlankNotice
          editProject={editProject}
          isVersionPdfsEnabled={isVersionPdfsEnabled}
        />
      : <ProjectActivityTable
          addInedibleToast={addInedibleToast}
          currentUser={currentUser}
          downloadDisabled={downloadDisabled}
          hasMore={hasNextHistoriesPage}
          isActive={!isFetchingNextHistoriesPage}
          isVersionPdfsEnabled={isVersionPdfsEnabled}
          loadMore={fetchNextHistoriesPage}
          messageInfo={formatMessageInfo(histories)}
          projectHistories={formatHistories(histories)}
          projectId={projectId}
        />
      }
      <ToastRack toasts={toasts} />
    </>
  );
}
